import {
  INVITE_STATUS,
  InviteMissingNavContactItemDto,
  InviteMissingNavContactsResultDto,
  MissingNavContactDto,
  MissingNavContactsDto,
} from "../responses/MissingNavContactsDto";

// Normalisers for "Contacts in the old webshop" (flag
// missingNavContactsEnabled). Both routes answer RAW JSON; every field is
// made present and typed here so the section never crashes on an older or
// newer API shape (the discipline of normalizeResendBatchResult). Rows
// without a positive integer contactId are dropped - they could never be
// invited.

const SOURCE_MIRROR = "mirror";

/** The statuses that mean "not created, nothing to retry" - counted as skipped. */
const SKIP_STATUSES: ReadonlySet<string> = new Set([
  INVITE_STATUS.skippedExists,
  INVITE_STATUS.invalidEmail,
  INVITE_STATUS.excludedDomain,
  INVITE_STATUS.notFound,
]);

/** The statuses after which a contact is no longer "missing" (it has a user now, or had one all along). */
const SETTLED_STATUSES: ReadonlySet<string> = new Set([
  INVITE_STATUS.created,
  INVITE_STATUS.skippedExists,
]);

function objectOf(raw: unknown): Record<string, unknown> {
  return raw !== null && typeof raw === "object"
    ? (raw as Record<string, unknown>)
    : {};
}

function rowsOf(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((row) => row !== null && typeof row === "object")
    .map((row) => row as Record<string, unknown>);
}

function idOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : null;
}

/** The rows that carry a usable contactId, paired with it (the rest are dropped). */
function rowsWithContactId(
  value: unknown,
): { row: Record<string, unknown>; contactId: number }[] {
  return rowsOf(value).flatMap((row) => {
    const contactId = idOrNull(row.contactId);
    return contactId === null ? [] : [{ row, contactId }];
  });
}

function countOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : undefined;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function textOrNull(value: unknown): string | null {
  const trimmed = text(value);
  return trimmed.length > 0 ? trimmed : null;
}

export function normalizeMissingNavContacts(
  raw: unknown,
  organizationId: number,
): MissingNavContactsDto {
  const body = objectOf(raw);
  const routeOrgId = idOrNull(body.organizationId) ?? organizationId;
  const contacts: MissingNavContactDto[] = rowsWithContactId(body.contacts)
    .map(({ row, contactId }) => {
      const orgId = idOrNull(row.orgId) ?? routeOrgId;
      return {
        contactId,
        contactNo: text(row.contactNo),
        name: text(row.name),
        email: text(row.email),
        phone: text(row.phone),
        isMain: row.isMain === true,
        orgId,
        orgName: text(row.orgName),
        orgNavNumber: text(row.orgNavNumber),
        orgCvr: text(row.orgCvr),
        isOwnOrg:
          typeof row.isOwnOrg === "boolean" ? row.isOwnOrg : orgId === routeOrgId,
      };
    });
  return {
    source: textOrNull(body.source) ?? SOURCE_MIRROR,
    organizationId: routeOrgId,
    contacts,
    withoutEmail: countOrUndefined(body.withoutEmail) ?? 0,
    truncated: body.truncated === true,
  };
}

function failedRow(contactId: number): InviteMissingNavContactItemDto {
  return {
    contactId,
    contactNo: "",
    status: INVITE_STATUS.failed,
    userId: null,
    mailSent: null,
    reason: null,
    message: null,
  };
}

/** created / skipped (every skip kind) / failed (the rest) over a list of rows. */
export function summarizeInvite(
  results: readonly InviteMissingNavContactItemDto[],
): { created: number; skipped: number; failed: number } {
  const created = results.filter((r) => r.status === INVITE_STATUS.created).length;
  const skipped = results.filter((r) => SKIP_STATUSES.has(r.status)).length;
  return { created, skipped, failed: results.length - created - skipped };
}

/**
 * The POST's answer, made whole: one row per requested id (an id the API
 * never answered is a failed row without a reason or message), the totals
 * taken from the API when it sent them and recomputed from the rows
 * otherwise. `reason` is the API's machine code, `message` its user-facing
 * text (MissingNavContactsPolicy) - the section shows only the latter.
 */
export function normalizeInviteResult(
  raw: unknown,
  requestedIds: readonly number[],
): InviteMissingNavContactsResultDto {
  const body = objectOf(raw);
  const answered: InviteMissingNavContactItemDto[] = rowsWithContactId(body.results)
    .map(({ row, contactId }) => ({
      contactId,
      contactNo: text(row.contactNo),
      status: textOrNull(row.status) ?? INVITE_STATUS.failed,
      userId: idOrNull(row.userId),
      mailSent: typeof row.mailSent === "boolean" ? row.mailSent : null,
      reason: textOrNull(row.reason),
      message: textOrNull(row.message),
    }));
  const answeredIds = new Set(answered.map((r) => r.contactId));
  const unanswered = requestedIds
    .filter((id) => !answeredIds.has(id))
    .map(failedRow);
  const results = answered.concat(unanswered);
  const summary = summarizeInvite(results);
  return {
    results,
    created: countOrUndefined(body.created) ?? summary.created,
    skipped: countOrUndefined(body.skipped) ?? summary.skipped,
    failed: countOrUndefined(body.failed) ?? summary.failed,
    mailNotSent:
      countOrUndefined(body.mailNotSent) ??
      results.filter(
        (r) => r.status === INVITE_STATUS.created && r.mailSent === false,
      ).length,
  };
}

/**
 * The cached list after a run: the created and already-existing contacts are
 * no longer missing and leave it (no second GET). The SAME array comes back
 * when nothing settled, so the caller can skip the cache write.
 */
export function removeSettledContacts(
  contacts: MissingNavContactDto[],
  results: readonly InviteMissingNavContactItemDto[],
): MissingNavContactDto[] {
  const settled = new Set(
    results.filter((r) => SETTLED_STATUSES.has(r.status)).map((r) => r.contactId),
  );
  if (settled.size === 0) return contacts;
  const next = contacts.filter((c) => !settled.has(c.contactId));
  return next.length === contacts.length ? contacts : next;
}

/** Positive ids, each once, in the order given (the API collapses duplicates the same way). */
export function uniqueContactIds(contactIds: readonly number[]): number[] {
  const seen = new Set<number>();
  return contactIds.filter((id) => {
    if (idOrNull(id) === null || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}
